/**
 * Voice handler for `refresh_current_page` — equivalent to clicking
 * the refresh icon at the top-right of the page (or F5 in the
 * browser). Mounted globally from NokiCopilot.jsx.
 *
 * Server preconditions `not_recording` + `not_in_recording_transition`
 * guard against refreshing during an active recording (which would
 * tear down the audio stream and lose the encounter). The agent
 * narrates a structured blocker; the FE handler is never called in
 * those cases.
 *
 * Capability matrix:
 *
 *   #  Voice                              Behaviour
 *   1  "refresh"                          window.location.reload()
 *   2  "reload the page"                  same
 *   3  "refresh the page"                 same
 *   4  "pull the latest"                  same
 *   5  (recording active)                 server: blocked / recording_in_progress
 *   6  (transition in flight)             server: blocked / busy
 *
 * Failure modes are server-resolved; the handler is purely action.
 */

import { useMemo } from "react";

import { useCopilotAction } from "./actionRegistry";

const DESCRIPTOR = Object.freeze({
  name: "refresh_current_page",
  description:
    "Implemented by noki-fe (window.location.reload()).",
  classification: "WRITE_SAFE",
  confirmStyle: "none",
  parameters: [],
});

export function useCopilotRefreshAction() {
  const descriptor = useMemo(() => DESCRIPTOR, []);

  useCopilotAction(descriptor, async () => {
    // eslint-disable-next-line no-console
    console.info("[copilot] refresh_current_page.invoke");
    if (typeof window === "undefined" || !window.location?.reload) {
      throw new Error("host_exception: window.location.reload not available.");
    }
    // Schedule on next tick so the WS round-trip can complete the
    // tool.result before the page tears down — otherwise the server
    // sees a host_timeout instead of a clean ok.
    window.setTimeout(() => {
      window.location.reload();
    }, 50);
    return { reloading: true };
  });
}
